
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './add_update_invoice.module.css';
import { ActionButton, Content, Dialog, DialogContainer, Divider, Flex, Heading, Item, Picker, View } from '@adobe/react-spectrum';
import { ToastQueue } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import AddInvoiceDetail from './formSections/InvoiceDetailSection';
import AddSupplierDetail from './formSections/InvoiceSupplierSection';
import AddBankInfo from './formSections/InvoiceBankSection';
import InvoiceShippingSection from './formSections/InvoiceShippingSection';
import InvoiceFinanceSection from './formSections/InvoiceFinanceSection';
import InvoiceCustomAttributesSection from './formSections/InvoiceCustomAttributes';
import InvoiceCommentSection from './formSections/InvoiceCommentSection';
import InvoiceCustomerSection from './formSections/InvoiceCustomerSection';
import { Invoice } from '../../../../services/soap/dataObjects/Invoice';
import InvoiceTransactionComponent from './formSections/InvoiceITransactionSection';
import { ChevronLeft } from 'react-bootstrap-icons';
import { Button, Spinner } from 'react-bootstrap';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { CreateInvoiceRequest } from '../../../../services/soap/invoice/requests/CreateInvoiceRequest';
import { UpdateInvoiceRequest } from '../../../../services/soap/invoice/requests/UpdateInvoiceRequest';
import { buildInvoicePayloadForSave } from '../../../../shared/invoice/transactionBuilder';
import { isPaidState } from '../../../../shared/invoice/invoiceStateUtils';
import { ListFormSettingsRequest } from '../../../../services/soap/features/requests/ListFormSettingsRequest';
import { GetFormSettingsRequest } from '../../../../services/soap/features/requests/GetFormSettingsRequest';
import { GetFinanceSettingsRequest } from '../../../../services/soap/finance/requests/GetFinanceSettingsRequest';
import { ListCurrencyRateRequest } from '../../../../services/soap/finance/requests/ListCurrencyRateRequest';
import { ListJournalRequest } from '../../../../services/soap/finance/requests/ListJournalRequest';
import { usePreloadAssets } from '../../../../hooks/UsePreloadAssets';
import { GetActivityRequest } from '../../../../services/soap/project/requests/GetActivityRequest';
import { GetProjectRequest } from '../../../../services/soap/project/requests/GetProjectRequest';
import { GetFolderRequest } from '../../../../services/soap/project/requests/GetFolderRequest';
import { GetContactRequest } from '../../../../services/soap/main/requests/GetContactRequest';
import { getInvoiceDocumentClass } from '../../../../shared/invoice/invoiceTypeLogic';
import { ContextSelection } from '../../../../components/protected/Search/ContextSelection';
import type { ContextObject } from '../../../../components/protected/Search/ContextSelection/types';
interface IAddUpdateInvoiceProps {
    selectedInvoice: any;
    isEdit?: boolean;
    handleBackClick?: () => void;
    onSaveSuccess?: (invoiceId?: any) => void | Promise<void>;
    SELECTED_INVOICE: any;
}
const AddUpdateInvoice: React.FC<IAddUpdateInvoiceProps> = ({
    selectedInvoice, isEdit, handleBackClick, onSaveSuccess, SELECTED_INVOICE
}) => {
    const { store, invoiceService, featureService, financeService, projectService, mainService } = useDependency();
    const {
        addressTypes,
        currencies,
        folderStatusMapping,
        projectStatusMapping,
        activityStatusMapping,
    } = usePreloadAssets();
    let [invoice, setInvoice] = useState<any>();
    const invoiceRef = useRef<any>();
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [showLeaveDialog, setShowLeaveDialog] = useState(false);
    const pendingLeaveActionRef = useRef<(() => void) | null>(null);
    const [selectedContext, setSelectedContext] = useState<any>(null);
    const [invoiceContextSelection, setInvoiceContextSelection] =
        useState<ContextObject | null>(null);
    const [invoiceStatusOptions, setInvoiceStatusOptions] = useState<any[]>([]);
    const defaultCurrencyAttemptedRef = useRef(false);
    const defaultJournalAttemptedRef = useRef(false);
    const defaultCounterpartyAttemptedRef = useRef(false);
    const defaultProviderAttemptedRef = useRef(false);
    const defaultCounterpartyLoadingRef = useRef(false);
    const defaultProviderLoadingRef = useRef(false);
    const manualCurrencyRateRef = useRef(false);
    const autoApplyingCurrencyRateRef = useRef(false);
    const lastLoadedCurrencyRateKeyRef = useRef('');
    const [defaultOrgCurrencyCode, setDefaultOrgCurrencyCode] = useState('');
    const { t } = useTranslation('invoice');
    const _t = (key: string, defaultValue?: string, options?: Record<string, any>) =>
        t(`add_update_invoice.${key}`, {
            ns: 'invoice',
            ...(defaultValue !== undefined ? { defaultValue } : {}),
            ...(options || {}),
        });
    const _tt = (key: string, defaultValue?: string) =>
        t(`invoice_common.${key}`, {
            ns: 'invoice',
            ...(defaultValue !== undefined ? { defaultValue } : {}),
        });

    const todayIso = () => new Date().toISOString().split('T')[0];
    const addDaysToIsoDate = (isoDate: string, days: number): string => {
        const [year, month, day] = String(isoDate).split('-').map(Number);
        if (!year || !month || !day) return isoDate;
        const base = new Date(Date.UTC(year, month - 1, day));
        if (Number.isNaN(base.getTime())) return isoDate;
        base.setUTCDate(base.getUTCDate() + Math.max(0, days));
        const y = base.getUTCFullYear();
        const m = String(base.getUTCMonth() + 1).padStart(2, '0');
        const d = String(base.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };
    const getDocumentTypeLabel = (currentInvoice: any): string => {
        const baseType = String(currentInvoice?.baseType || SELECTED_INVOICE?.baseType || '');
        const isIssued = String(currentInvoice?.isIssued || SELECTED_INVOICE?.isIssued || '');

        if (baseType === '4' && isIssued === '0') return _t('doc_estimate', 'Estimate');
        if (baseType === '8' && isIssued === '0') return _t('doc_received_offer', 'Received Offer');
        if (baseType === '2' && isIssued === '0') return _t('doc_sales_order', 'Sales Order');
        if (baseType === '2' && isIssued === '1') return _t('doc_purchase_order', 'Purchase Order');
        if (baseType === '1' && isIssued === '1') return _t('doc_issued_invoice', 'Issued Invoice');
        if (baseType === '1' && isIssued === '0') return _t('doc_received_invoice', 'Received Invoice');
        return _t('doc_invoice', 'Invoice');
    };
    const getHeaderTitle = (): string => {
        const docType = getDocumentTypeLabel(invoice || selectedInvoice || SELECTED_INVOICE);
        return isEdit
            ? _t('title_update_doc', 'Update {{docType}}', { docType })
            : _t('title_add_doc', 'Add {{docType}}', { docType });
    };
    useEffect(() => {
        invoiceRef.current = invoice;
    }, [invoice]);

    const getContextIdsFromSelection = useCallback((selection: ContextObject | null) => {
        const next = {
            activityId: '',
            projectId: '',
            folderId: '',
        };

        if (!selection) return next;

        selection.pathSegmentContexts?.forEach((segment) => {
            const id = String(segment.id ?? '').trim();
            const type = String(segment.type ?? '').toUpperCase();
            if (!id) return;
            if (type === 'FOLDER') next.folderId = id;
            if (type === 'PROJECT') next.projectId = id;
            if (type === 'ACTIVITY') next.activityId = id;
        });

        const selectedId = String(selection.contextId ?? '').trim();
        const selectedType = String(selection.contextType ?? '').toUpperCase();
        if (selectedId) {
            if (selectedType === 'FOLDER') next.folderId = selectedId;
            if (selectedType === 'PROJECT') next.projectId = selectedId;
            if (selectedType === 'ACTIVITY') next.activityId = selectedId;
        }

        return next;
    }, []);

    const contextSelectionDefaultValue = React.useMemo(() => {
        // IMPORTANT: In Invoice "Add" flow, `SELECTED_INVOICE.folder` is reused as a
        // counterparty seed (customer/provider) in existing logic, so it is not a
        // reliable context node id. Only use persisted invoice context fields.
        const rawCandidates = isEdit
            ? [
                invoice?.activity,
                invoice?.project,
                invoice?.folder,
                selectedInvoice?.activity,
                selectedInvoice?.project,
                selectedInvoice?.folder,
            ]
            : [invoice?.activity, invoice?.project, invoice?.folder];

        const raw = rawCandidates
            .map((v) => String(v ?? '').trim())
            .find((v) => v && v !== '0') || '';

        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : null;
    }, [
        isEdit,
        invoice?.activity,
        invoice?.folder,
        invoice?.project,
        selectedInvoice?.activity,
        selectedInvoice?.folder,
        selectedInvoice?.project,
    ]);

    const contextSelectionFallbackValue = React.useMemo((): ContextObject | null => {
        const toPositiveId = (value: unknown): number | null => {
            const raw = String(value ?? '').trim();
            if (!raw || raw === '0') return null;
            const parsed = Number(raw);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        };

        const activityId = toPositiveId(
            isEdit
                ? (invoice?.activity || selectedInvoice?.activity)
                : invoice?.activity
        );
        const projectId = toPositiveId(
            isEdit
                ? (invoice?.project || selectedInvoice?.project)
                : invoice?.project
        );
        const folderId = toPositiveId(
            isEdit
                ? (invoice?.folder || selectedInvoice?.folder)
                : invoice?.folder
        );

        const folderName = String(
            isEdit
                ? (invoice?.folderName || selectedInvoice?.folderName || '')
                : (invoice?.folderName || '')
        ).trim();
        const projectName = String(
            isEdit
                ? (
                    invoice?.projectName ||
                    invoice?.ProjectName ||
                    selectedInvoice?.projectName ||
                    selectedInvoice?.ProjectName ||
                    ''
                )
                : (invoice?.projectName || invoice?.ProjectName || '')
        ).trim();
        const activityName = String(
            isEdit
                ? (invoice?.activityName || selectedInvoice?.activityName || '')
                : (invoice?.activityName || '')
        ).trim();

        const pathSegments: string[] = [];
        const pathSegmentContexts: NonNullable<ContextObject['pathSegmentContexts']> = [];

        if (folderId && folderName) {
            pathSegments.push(folderName);
            pathSegmentContexts.push({ id: folderId, type: 'FOLDER' });
        }
        if (projectId && projectName) {
            pathSegments.push(projectName);
            pathSegmentContexts.push({ id: projectId, type: 'PROJECT' });
        }
        if (activityId && activityName) {
            pathSegments.push(activityName);
            pathSegmentContexts.push({ id: activityId, type: 'ACTIVITY' });
        }

        const leaf = pathSegmentContexts[pathSegmentContexts.length - 1];
        if (!leaf || pathSegments.length === 0) return null;

        return {
            contextId: leaf.id,
            contextType: leaf.type,
            link: '',
            pathSegments,
            pathSegmentContexts,
        };
    }, [
        isEdit,
        invoice?.ProjectName,
        invoice?.activity,
        invoice?.activityName,
        invoice?.folder,
        invoice?.folderName,
        invoice?.project,
        invoice?.projectName,
        selectedInvoice?.ProjectName,
        selectedInvoice?.activity,
        selectedInvoice?.activityName,
        selectedInvoice?.folder,
        selectedInvoice?.folderName,
        selectedInvoice?.project,
        selectedInvoice?.projectName,
    ]);

    const onContextPathResolveError = useCallback((error: unknown) => {
        console.error('Invoice context resolve failed', error);
    }, []);
    const setInvoiceWithRef = useCallback((value: React.SetStateAction<any>) => {
        setInvoice((prev: any) => {
            const next = typeof value === 'function' ? (value as (prevState: any) => any)(prev) : value;
            invoiceRef.current = next;
            return next;
        });
        setIsDirty(true);
    }, []);

    const handleContextSelectionChange = useCallback((selection: ContextObject | null) => {
        setInvoiceContextSelection(selection);
        const ids = getContextIdsFromSelection(selection);
        setInvoiceWithRef((prev: any) => ({
            ...(prev || {}),
            folder: ids.folderId,
            project: ids.projectId,
            activity: ids.activityId,
        }));
    }, [getContextIdsFromSelection, setInvoiceWithRef]);

    const getDefaultNodeByIdents = useCallback((idents: string[]): string | undefined => {
        const defaults = store?.session?.organization?.DEFAULTS?.DEFAULT;
        const list = Array.isArray(defaults) ? defaults : defaults ? [defaults] : [];
        if (!list.length) return undefined;

        for (const ident of idents) {
            const found = list.find((d: any) => String(d?.ident || '').toUpperCase() === ident.toUpperCase());
            const node = found?.node || found?.value;
            if (node) return String(node);
        }
        return undefined;
    }, [store?.session?.organization?.DEFAULTS?.DEFAULT]);

    const resolveInvoiceTreeHandleFromDefaults = (currentInvoice: any): string | undefined => {
        const baseType = String(currentInvoice?.baseType || SELECTED_INVOICE?.baseType || '');
        const isIssued = String(currentInvoice?.isIssued || SELECTED_INVOICE?.isIssued || '');

        if (baseType === '1' && isIssued === '1') {
            return getDefaultNodeByIdents(['INVOICEISSUEDNODE', 'INVOICENODE']);
        }
        if (baseType === '1' && isIssued === '0') {
            return getDefaultNodeByIdents(['INVOICERECEIVEDNODE']);
        }
        if (baseType === '2' && isIssued === '1') {
            return getDefaultNodeByIdents(['ORDERNODE']);
        }
        if (baseType === '2' && isIssued === '0') {
            return getDefaultNodeByIdents(['ORDERRECEIVEDNODE']);
        }
        if (baseType === '4') {
            return getDefaultNodeByIdents(['BUDGETNODE']);
        }
        if (baseType === '8') {
            return getDefaultNodeByIdents(['ORDERRECEIVEDNODE']);
        }

        return getDefaultNodeByIdents([
            'INVOICEISSUEDNODE',
            'INVOICENODE',
            'INVOICERECEIVEDNODE',
            'ORDERNODE',
            'ORDERRECEIVEDNODE',
            'BUDGETNODE',
        ]);
    };

    const resolveTransactionTreeHandleFromDefaults = (): string | undefined => {
        return getDefaultNodeByIdents([
            'INVOICETRANSACTIONNODE',
            'GLITEMNODE',
            'TRANSACTIONNODE',
            'ENTRYNODE',
            'FINENTRYNODE',
            'FINANCENODE',
        ]);
    };

    const isIssuedState = (statusRow: any): boolean => {
        const valueCandidates = [
            statusRow?.value,
            statusRow?.stateValue,
            statusRow?.statusValue,
            statusRow?.fixedStatus,
            statusRow?.code,
        ]
            .map((v: any) => String(v ?? '').trim())
            .filter((v: string) => v.length > 0);

        if (valueCandidates.some((v: string) => v === '1')) {
            return true;
        }

        const name = String(statusRow?.name ?? '').trim().toLowerCase();
        return name.includes('issued') || name.includes('vystaveno');
    };

    const resolveDefaultInvoiceState = (
        currentInvoice: any,
        preferIssuedState: boolean = false
    ): { state?: string; stateName?: string } => {
        if (currentInvoice?.state) {
            return {
                state: String(currentInvoice.state),
                stateName: currentInvoice?.stateName,
            };
        }

        const baseType = String(currentInvoice?.baseType || SELECTED_INVOICE?.baseType || '');
        const listRaw = store?.session?.organization?.OBJECT_SETTINGS?.INVOICE?.INVOICE_STATES?.INVOICE_STATUS;
        const list = Array.isArray(listRaw) ? listRaw : listRaw ? [listRaw] : [];
        if (!list.length) {
            return {};
        }

        const filtered = list
            .filter((s: any) => String(s?.enabled ?? '1') !== '0')
            .filter((s: any) => !baseType || String(s?.status ?? '') === baseType);

        const source = (filtered.length > 0 ? filtered : list).slice().sort((a: any, b: any) => {
            const pa = Number(a?.priority ?? 9999);
            const pb = Number(b?.priority ?? 9999);
            return pa - pb;
        });

        const first = source[0];
        const issuedCandidate = preferIssuedState ? source.find((s: any) => isIssuedState(s)) : undefined;
        const selectedState = issuedCandidate || first;
        if (!selectedState?.id) return {};
        return {
            state: String(selectedState.id),
            stateName: selectedState?.name || '',
        };
    };

    const getInvoiceStatusOptions = (currentInvoice: any) => {
        const listRaw = store?.session?.organization?.OBJECT_SETTINGS?.INVOICE?.INVOICE_STATES?.INVOICE_STATUS;
        const allStatuses = (Array.isArray(listRaw) ? listRaw : listRaw ? [listRaw] : [])
            .filter((s: any) => String(s?.enabled ?? '1') !== '0')
            .sort((a: any, b: any) => Number(a?.priority ?? 9999) - Number(b?.priority ?? 9999));

        const allowedRaw = currentInvoice?.STATES?.STATUS;
        const allowed = Array.isArray(allowedRaw) ? allowedRaw : allowedRaw ? [allowedRaw] : [];

        // For update mode, prefer BE-provided allowed statuses (dynamic by journal/user).
        // If missing, fall back to all enabled statuses from OBJECT_SETTINGS.
        const source =
            !!isEdit && allowed.length > 0
                ? allowed
                    .map((a: any) => {
                        const id = String(a?.state ?? a?.id ?? '');
                        const fromAll = allStatuses.find((s: any) => String(s?.id ?? '') === id);
                        return {
                            id,
                            name: fromAll?.name || a?.name || id,
                            priority: Number(a?.priority ?? fromAll?.priority ?? 9999),
                        };
                    })
                    .filter((s: any) => !!s.id)
                    .sort((a: any, b: any) => a.priority - b.priority)
                : allStatuses.map((s: any) => ({
                    id: String(s?.id ?? ''),
                    name: s?.name || String(s?.id ?? ''),
                    priority: Number(s?.priority ?? 9999),
                }));

        return source.map((s: any) => ({
            id: String(s?.id ?? ''),
            name: s?.name || String(s?.id ?? ''),
        }));
    };

    const getJournalValue = (journal: any, ...keys: string[]) => {
        for (const key of keys) {
            const val = journal?.[key];
            if (val !== undefined && val !== null && val !== '') return val;
        }
        return undefined;
    };
    const resolveJournalById = useCallback(async (journalId?: string) => {
        const normalizedJournalId = String(journalId || '').trim();
        if (!normalizedJournalId) return undefined;

        try {
            const response: any = await financeService.listJournal(
                new ListJournalRequest(store.Server, store.SessionId)
            );
            const items = Array.isArray(response?.JOURNAL) ? response.JOURNAL : [];
            return items.find((item: any) => String(item?.id ?? '') === normalizedJournalId);
        } catch {
            return undefined;
        }
    }, [financeService, store.Server, store.SessionId]);

    const areStatusOptionsEqual = (left: any[], right: any[]) => {
        if (left === right) return true;
        if (!Array.isArray(left) || !Array.isArray(right)) return false;
        if (left.length !== right.length) return false;

        for (let i = 0; i < left.length; i += 1) {
            if (String(left[i]?.id ?? '') !== String(right[i]?.id ?? '')) return false;
            if (String(left[i]?.name ?? '') !== String(right[i]?.name ?? '')) return false;
        }
        return true;
    };

    const getInvoiceFormType = (currentInvoice: any): string => {
        const baseType = String(currentInvoice?.baseType || SELECTED_INVOICE?.baseType || '');
        const isIssued = String(currentInvoice?.isIssued || SELECTED_INVOICE?.isIssued || '');
        if (baseType === '1' && isIssued === '0') return 'TEditInvoiceReceived';
        if (baseType === '1' && isIssued === '1') return 'TEditInvoiceIssued';
        if (baseType === '2' && isIssued === '0') return 'TEditOrderReceived';
        if (baseType === '2' && isIssued === '1') return 'TEditOrderIssued';
        if (baseType === '4') return 'TEditBudgetIssued';
        if (baseType === '8') return 'TEditOfferReceived';
        return 'TEditInvoiceIssued';
    };

    const readDefaultDueDaysFromItems = (items: any[]): number | undefined => {
        if (!Array.isArray(items) || items.length === 0) return undefined;
        const extractNumeric = (raw: any): number | undefined => {
            const numeric = Number(String(raw ?? '').trim());
            if (!Number.isFinite(numeric)) return undefined;
            return Math.max(0, numeric);
        };

        const parseFromOtherParams = (otherParams: string): number | undefined => {
            if (!otherParams) return undefined;
            const dueRegex = /due[_\s-]*days?[^0-9-]*(-?\d+)/i;
            const match = otherParams.match(dueRegex);
            if (match?.[1] !== undefined) {
                const parsed = Number(match[1]);
                if (Number.isFinite(parsed)) return Math.max(0, parsed);
            }
            return undefined;
        };

        const normalized = items.map((item: any) => {
            const search = `${String(item?.itemname ?? '')} ${String(item?.colname ?? '')} ${String(item?.name ?? '')} ${String(item?.caption ?? '')}`.toLowerCase();
            const fromDefault = extractNumeric(item?.defaultval);
            const fromOtherParams = parseFromOtherParams(String(item?.otherparams ?? ''));
            return {
                item,
                search,
                dueDays: fromDefault ?? fromOtherParams,
            };
        });

        const preferred = normalized.find((entry) =>
            (entry.search.includes('due') && entry.search.includes('day')) ||
            entry.search.includes('duedays') ||
            entry.search.includes('due_days') ||
            entry.search.includes('splatnost') ||
            entry.search.includes('maturity')
        );

        const fallbackAnyNumeric = normalized.find((entry) => entry.dueDays !== undefined);
        const chosen = preferred || fallbackAnyNumeric;

        if (!chosen || chosen.dueDays === undefined) {
            return undefined;
        }
        return chosen.dueDays;
    };

    const getAddFormSettings = useCallback(
        async (settingId: string, formType: string): Promise<number | undefined> => {
            let resolvedSettingId = settingId;
            if (resolvedSettingId === '0') {
                const resFormSettings = await featureService.listFormSettings(
                    new ListFormSettingsRequest(store.Server, store.SessionId)
                );
                const settings = Array.isArray(resFormSettings?.SETTING) ? resFormSettings.SETTING : [];
                const byForm = settings.filter((item: any) => String(item?.formId || '') === formType);
                const byInvoiceForms = settings.filter((item: any) =>
                    String(item?.formId || '').toLowerCase().includes('invoice')
                );
                const defaultSetting =
                    byForm.find((item: any) => String(item?.isDefault) === '1') ||
                    byInvoiceForms.find((item: any) => String(item?.isDefault) === '1') ||
                    byForm[0] ||
                    byInvoiceForms[0] ||
                    settings.find((item: any) => String(item?.isDefault) === '1');
                if (defaultSetting?.id) {
                    resolvedSettingId = String(defaultSetting.id);
                }
            }

            if (!resolvedSettingId || resolvedSettingId === '0') {
                return undefined;
            }

            const response = await featureService.getFormSettings(
                new GetFormSettingsRequest(store.Server, store.SessionId, resolvedSettingId)
            );
            if (response?.result !== 'OK' || !response?.SETTING?.length) {
                return undefined;
            }

            const items = response.SETTING[0]?.ITEM || [];
            return readDefaultDueDaysFromItems(items);
        },
        [featureService, store.Server, store.SessionId]
    );

    const getOrgId = useCallback((): string => {
        const id =
            store?.session?.organization?.id ??
            store?.session?.sessionInfo?.organizationId ??
            '';
        return String(id || '').trim();
    }, [store?.session?.organization?.id, store?.session?.sessionInfo?.organizationId]);

    const getFinanceCurrencyCacheKey = useCallback((): string => {
        const orgId = getOrgId() || 'unknown';
        return `finance_default_currency_${store.Server}_${orgId}`;
    }, [getOrgId, store.Server]);

    const readCachedDefaultCurrency = useCallback((): string | undefined => {
        try {
            const raw = window.localStorage.getItem(getFinanceCurrencyCacheKey());
            if (!raw) return undefined;
            const trimmed = String(raw).trim();
            if (!trimmed) return undefined;
            if (trimmed.startsWith('{')) {
                const parsed = JSON.parse(trimmed);
                const value = String(parsed?.currency || '').trim();
                return value || undefined;
            }
            return trimmed;
        } catch {
            return undefined;
        }
    }, [getFinanceCurrencyCacheKey]);

    const writeCachedDefaultCurrency = useCallback((currencyCode: string) => {
        const code = String(currencyCode || '').trim();
        if (!code) return;
        try {
            window.localStorage.setItem(
                getFinanceCurrencyCacheKey(),
                JSON.stringify({ currency: code, updatedAt: Date.now() })
            );
        } catch {
            // Ignore local storage errors.
        }
    }, [getFinanceCurrencyCacheKey]);

    useEffect(() => {
        let active = true;

        const loadDefaultOrgCurrencyCode = async () => {
            const cached = readCachedDefaultCurrency();
            if (cached) {
                if (active) setDefaultOrgCurrencyCode(cached);
                return;
            }

            try {
                const response: any = await financeService.getFinanceSettings(
                    new GetFinanceSettingsRequest(store.Server, store.SessionId)
                );
                const settings = response?.SETTINGS;
                const defaultCurrency = String(
                    Array.isArray(settings) ? settings?.[0]?.currency : settings?.currency || ''
                ).trim();
                if (!defaultCurrency || !active) return;
                writeCachedDefaultCurrency(defaultCurrency);
                setDefaultOrgCurrencyCode(defaultCurrency);
            } catch {
                // Ignore and keep form usable without auto-rate lookup.
            }
        };

        loadDefaultOrgCurrencyCode();
        return () => {
            active = false;
        };
    }, [financeService, store.Server, store.SessionId, readCachedDefaultCurrency, writeCachedDefaultCurrency]);

    const toPositiveInteger = useCallback((raw: any): number | undefined => {
        const parsed = Number(String(raw ?? '').trim());
        if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
        return Math.trunc(parsed);
    }, []);

    const toNonEmptyString = useCallback((raw: any): string | undefined => {
        const value = String(raw ?? '').trim();
        return value || undefined;
    }, []);

    const getCurrencyRecordByCode = useCallback((currencyCode: string) => {
        const code = String(currencyCode || '').trim().toUpperCase();
        if (!code) return undefined;
        return (currencies || []).find((item: any) =>
            String(item?.code || '').trim().toUpperCase() === code
        );
    }, [currencies]);

    const normalizeIsoDate = useCallback((raw: any): string => {
        const value = String(raw || '').trim();
        if (!value) return todayIso();
        return value.includes('T') ? value.split('T')[0] : value;
    }, []);

    const getEffectiveCurrencyRateDate = useCallback((currentInvoice: any): string => {
        return normalizeIsoDate(
            currentInvoice?.dateTransaction ||
            currentInvoice?.taxationDate ||
            currentInvoice?.issuedDate ||
            todayIso()
        );
    }, [normalizeIsoDate]);

    const getBestCurrencyRate = useCallback((response: any, targetDateIso: string) => {
        const rawRates = response?.currencyRate;
        const rates = Array.isArray(rawRates) ? rawRates : rawRates ? [rawRates] : [];
        if (!rates.length) return undefined;

        const target = new Date(`${targetDateIso}T00:00:00Z`).getTime();
        if (Number.isNaN(target)) return rates[0];

        const normalized = rates
            .map((rate: any) => {
                const iso = normalizeIsoDate(rate?.rateValidFrom);
                return {
                    source: rate,
                    validFromIso: iso,
                    validFromMs: new Date(`${iso}T00:00:00Z`).getTime(),
                };
            })
            .filter((entry: any) => !Number.isNaN(entry.validFromMs))
            .sort((a: any, b: any) => a.validFromMs - b.validFromMs);

        if (!normalized.length) return rates[0];

        const eligible = normalized.filter((entry: any) => entry.validFromMs <= target);
        return (eligible.length ? eligible[eligible.length - 1] : normalized[0]).source;
    }, [normalizeIsoDate]);

    const handleInvoiceDetailChange = useCallback((nextInvoice: any) => {
        setInvoice((prev: any) => {
            const prevInvoice = prev || {};
            const resolvedNext =
                typeof nextInvoice === 'function' ? nextInvoice(prevInvoice) : nextInvoice;
            if (!resolvedNext) return resolvedNext;

            const prevCurrency = String(prevInvoice?.currency || '').trim();
            const nextCurrency = String(resolvedNext?.currency || '').trim();
            const prevDate = normalizeIsoDate(prevInvoice?.dateTransaction);
            const nextDate = normalizeIsoDate(resolvedNext?.dateTransaction);
            const prevRate = String(prevInvoice?.currencyRate ?? '').trim();
            const nextRate = String(resolvedNext?.currencyRate ?? '').trim();

            if (prevCurrency !== nextCurrency || prevDate !== nextDate) {
                manualCurrencyRateRef.current = false;
                lastLoadedCurrencyRateKeyRef.current = '';
            }

            if (prevRate !== nextRate) {
                if (autoApplyingCurrencyRateRef.current) {
                    autoApplyingCurrencyRateRef.current = false;
                } else {
                    manualCurrencyRateRef.current = true;
                    lastLoadedCurrencyRateKeyRef.current = '';
                }
            }

            return resolvedNext;
        });
        setIsDirty(true);
    }, [normalizeIsoDate]);

    const firstDefinedString = useCallback(
        (...values: any[]): string | undefined => {
            for (const value of values) {
                const normalized = toNonEmptyString(value);
                if (normalized) return normalized;
            }
            return undefined;
        },
        [toNonEmptyString]
    );

    const firstDefinedId = useCallback(
        (...values: any[]): string | undefined => {
            for (const value of values) {
                const normalized = String(value ?? '').trim();
                if (!normalized) continue;
                const lowered = normalized.toLowerCase();
                if (normalized === '0' || lowered === 'null' || lowered === 'undefined') continue;
                return normalized;
            }
            return undefined;
        },
        []
    );

    const getCurrentUserDefaults = useCallback(() => {
        const sessionInfo = store?.session?.sessionInfo;
        const fullName = String(
            `${sessionInfo?.firstName ?? ''} ${sessionInfo?.lastName ?? ''}`.trim() || store?.Username || ''
        ).trim();

        return {
            userId: String(store?.UserId ?? '').trim(),
            userName: fullName,
        };
    }, [store?.UserId, store?.Username, store?.session?.sessionInfo]);

    const normalizeAddresses = useCallback((addressesWrapper: any): any[] => {
        if (!addressesWrapper) return [];
        let raw = addressesWrapper;
        if (typeof raw === 'object' && !Array.isArray(raw) && raw.ADDRESS) {
            raw = raw.ADDRESS;
        }
        const list = Array.isArray(raw) ? raw : [raw];
        return list.map((x: any) => x?.ADDRESS ?? x).filter(Boolean);
    }, []);

    const pickAddress = useCallback(
        (entityType: 'COMPANY' | 'PERSON', addressesWrapper: any) => {
            const list = normalizeAddresses(addressesWrapper);
            if (!list.length) return null;

            const byAddressType = list.find((address: any) => {
                const typeId = String(address?.type ?? '');
                const addressType = (addressTypes as any)?.[typeId];
                if (!addressType) return false;
                return entityType === 'COMPANY'
                    ? String(addressType?.invoiceAddress) === '1'
                    : String(addressType?.permanentAddress) === '1';
            });
            if (byAddressType) return byAddressType;

            const primary = list.find((address: any) => String(address?.isPrimary) === '1');
            if (primary) return primary;

            return list[0];
        },
        [addressTypes, normalizeAddresses]
    );

    const loadContactByFolderId = useCallback(
        async (folderId?: string) => {
            const idNumeric = toPositiveInteger(folderId);
            if (!idNumeric) return undefined;

            try {
                const response: any = await mainService.getContact(
                    new GetContactRequest(store.Server, store.SessionId, undefined, undefined, undefined, idNumeric)
                );
                if (response?.result === 'OK') {
                    const isPerson = !!response?.PERSON;
                    const entity = isPerson ? response.PERSON : response.COMPANY;
                    const entityType: 'PERSON' | 'COMPANY' = isPerson ? 'PERSON' : 'COMPANY';
                    const address = pickAddress(entityType, entity?.ADDRESSES);

                    return {
                        id: String(idNumeric),
                        name: String(entity?.name1 || entity?.name || '').trim(),
                        street: String(address?.street || '').trim(),
                        city: String(address?.cityName || address?.city || '').trim(),
                        zip: String(address?.zipName || address?.zip || '').trim(),
                        region: String(address?.regionName || address?.region || '').trim(),
                        country: String(address?.countryName || address?.country || '').trim(),
                    };
                }
            } catch {
                // Fallback to folder data below.
            }

            try {
                const folderResponse: any = await projectService.getFolder(
                    new GetFolderRequest(store.Server, store.SessionId, idNumeric)
                );
                if (folderResponse?.result !== 'OK') return undefined;

                const folder = folderResponse?.FOLDER;
                const company = folder?.CONTACT?.COMPANY;
                const rawAddresses = company?.ADDRESSES?.ADDRESS;
                const primaryAddress = Array.isArray(rawAddresses)
                    ? rawAddresses.find((a: any) => String(a?.isPrimary ?? '') === '1') || rawAddresses[0]
                    : rawAddresses || {};

                return {
                    id: String(idNumeric),
                    name: String(folder?.name || folder?.name1 || company?.name1 || company?.name || '').trim(),
                    street: String(primaryAddress?.street || company?.street || '').trim(),
                    city: String(primaryAddress?.cityName || primaryAddress?.city || company?.city || '').trim(),
                    zip: String(primaryAddress?.zipName || primaryAddress?.zip || company?.zip || '').trim(),
                    region: String(primaryAddress?.regionName || primaryAddress?.region || company?.region || '').trim(),
                    country: String(primaryAddress?.countryName || primaryAddress?.country || company?.country || '').trim(),
                };
            } catch {
                return undefined;
            }
        },
        [mainService, pickAddress, projectService, store.Server, store.SessionId, toPositiveInteger]
    );

    const mergeCounterpartyDefaults = useCallback(
        (prev: any, values: { provider?: any; customer?: any }) => {
            if (!prev) return prev;

            const next: any = { ...prev };
            const normalizeExistingId = (raw: any): string | undefined => {
                const value = String(raw ?? '').trim();
                if (!value) return undefined;
                const lowered = value.toLowerCase();
                if (value === '0' || lowered === 'null' || lowered === 'undefined') {
                    return undefined;
                }
                return value;
            };
            const currentProvider = normalizeExistingId(prev?.provider);
            const currentCustomer = normalizeExistingId(prev?.customer);

            if (!currentProvider && values.provider?.id) {
                next.provider = values.provider.id;
                next.providerName = values.provider.name || next.providerName || '';
                next.providerStreet = values.provider.street || '';
                next.providerCity = values.provider.city || '';
                next.providerZIP = values.provider.zip || '';
                next.providerRegion = values.provider.region || '';
                next.providerCountry = values.provider.country || '';
            }

            if (!currentCustomer && values.customer?.id) {
                next.customer = values.customer.id;
                next.customerName = values.customer.name || next.customerName || '';
                next.customerStreet = values.customer.street || '';
                next.customerCity = values.customer.city || '';
                next.customerZIP = values.customer.zip || '';
                next.customerRegion = values.customer.region || '';
                next.customerCountry = values.customer.country || '';
            }

            return next;
        },
        []
    );


    useEffect(() => {
        defaultCurrencyAttemptedRef.current = false;
        defaultJournalAttemptedRef.current = false;
        defaultCounterpartyAttemptedRef.current = false;
        defaultProviderAttemptedRef.current = false;
        defaultCounterpartyLoadingRef.current = false;
        defaultProviderLoadingRef.current = false;
        setIsDirty(false);
        setShowLeaveDialog(false);
        pendingLeaveActionRef.current = null;
        if (isEdit && selectedInvoice) {
            setInvoice({
                ...selectedInvoice,
                customerDueDays:
                    selectedInvoice?.customerDueDays !== undefined && selectedInvoice?.customerDueDays !== null
                        ? Math.max(0, Number(selectedInvoice.customerDueDays) || 0)
                        : selectedInvoice?.customerDueDays,
                invoiceDefaultDueDays:
                    selectedInvoice?.invoiceDefaultDueDays !== undefined && selectedInvoice?.invoiceDefaultDueDays !== null
                        ? Math.max(0, Number(selectedInvoice.invoiceDefaultDueDays) || 0)
                        : selectedInvoice?.invoiceDefaultDueDays,
            });
        }
        else {
            const today = todayIso();
            const initialDocClass = getInvoiceDocumentClass(SELECTED_INVOICE || {});
            const contextFolderId = SELECTED_INVOICE?.folder;
            const contextFolderName = SELECTED_INVOICE?.folderName;
            setInvoice({
                baseType: SELECTED_INVOICE?.baseType,
                isIssued: SELECTED_INVOICE?.isIssued,
                folder: SELECTED_INVOICE?.folder,
                folderName: SELECTED_INVOICE?.folderName,
                customer: initialDocClass === 'sales' ? contextFolderId : undefined,
                customerName: initialDocClass === 'sales' ? contextFolderName : undefined,
                provider: initialDocClass === 'purchase' ? contextFolderId : undefined,
                providerName: initialDocClass === 'purchase' ? contextFolderName : undefined,
                project: SELECTED_INVOICE?.project,
                activity: SELECTED_INVOICE?.activity,
                issuedDate: today,
                dueDate: today,
                invoiceDefaultDueDays: undefined,
                taxationDate: today,
                dateTransaction: today,
                periodFrom: today,
            } as Invoice);
        }
    }, [
        isEdit,
        selectedInvoice?.id,
        selectedInvoice?.modified,
        selectedInvoice?.customerDueDays,
        selectedInvoice?.invoiceDefaultDueDays,
        SELECTED_INVOICE?.baseType,
        SELECTED_INVOICE?.isIssued,
        SELECTED_INVOICE?.folder,
        SELECTED_INVOICE?.folderName,
        SELECTED_INVOICE?.project,
        SELECTED_INVOICE?.activity,
    ]);

    useEffect(() => {
        if (isEdit) return;
        if (!invoice) return;
        if (defaultJournalAttemptedRef.current) return;

        const existingJournal = String(invoice?.journal || '').trim();
        if (existingJournal) {
            defaultJournalAttemptedRef.current = true;
            return;
        }

        const journalType = String(SELECTED_INVOICE?.journalType || '').trim();
        if (!journalType) return;

        let active = true;
        const loadDefaultJournal = async () => {
            try {
                const response: any = await financeService.listJournal(
                    new ListJournalRequest(store.Server, store.SessionId)
                );
                let items = Array.isArray(response?.JOURNAL) ? response.JOURNAL : [];
                items = items.filter((x: any) => String(x?.type ?? '') === journalType);
                const preferred =
                    items.find((x: any) => String(x?.isPrimary ?? '') === '1') ||
                    items[0];

                if (!active || !preferred?.id) return;

                defaultJournalAttemptedRef.current = true;
                setInvoice((prev: any) => {
                    if (!prev) return prev;
                    if (String(prev?.journal || '').trim()) return prev;

                    return {
                        ...prev,
                        journal: preferred.id,
                        journalName: preferred.name,
                        journalTreeHandle: getJournalValue(preferred, 'treeHandle', 'treehandle') ?? prev?.journalTreeHandle,
                        accountBase: getJournalValue(preferred, 'accountBase', 'account_base') ?? prev?.accountBase,
                        accountBaseName: getJournalValue(preferred, 'accountBaseName', 'account_base_name') ?? prev?.accountBaseName,
                        accountRound: getJournalValue(preferred, 'accountRound', 'account_round') ?? prev?.accountRound,
                        accountRoundName: getJournalValue(preferred, 'accountRoundName', 'account_round_name') ?? prev?.accountRoundName,
                        accountTotal: getJournalValue(preferred, 'accountTotal', 'account_total') ?? prev?.accountTotal,
                        accountTotalName: getJournalValue(preferred, 'accountTotalName', 'account_total_name') ?? prev?.accountTotalName,
                        accountVAT: getJournalValue(preferred, 'accountVAT', 'account_vat') ?? prev?.accountVAT,
                        accountVATName: getJournalValue(preferred, 'accountVATName', 'account_vat_name') ?? prev?.accountVATName,
                        templateAcc: getJournalValue(preferred, 'templateAcc', 'template_acc') ?? prev?.templateAcc,
                    };
                });
            } catch {
                // Keep add form usable even if journal defaults fail.
            }
        };

        loadDefaultJournal();
        return () => {
            active = false;
        };
    }, [
        isEdit,
        invoice?.journal,
        SELECTED_INVOICE?.journalType,
        financeService,
        store.Server,
        store.SessionId,
    ]);

    useEffect(() => {
        if (isEdit) return;
        if (!invoice) return;
        if (defaultProviderAttemptedRef.current) return;
        if (defaultProviderLoadingRef.current) return;

        const docClass = getInvoiceDocumentClass(invoice || SELECTED_INVOICE || {});
        if (docClass !== 'sales') return;

        const existingProvider = String(invoice?.provider ?? '').trim();
        if (existingProvider && existingProvider !== '0') {
            defaultProviderAttemptedRef.current = true;
            return;
        }

        let active = true;
        const seedDefaultProvider = async () => {
            defaultProviderLoadingRef.current = true;
            const organizationSubjectNode = firstDefinedId(getDefaultNodeByIdents(['ORGANIZATIONSUBJECTNODE']));
            const currentUser = getCurrentUserDefaults();

            if (!organizationSubjectNode) {
                if (!active) return;
                defaultProviderAttemptedRef.current = true;
                setInvoice((prev: any) => {
                    if (!prev) return prev;
                    if (String(prev?.provider ?? '').trim()) return prev;
                    return {
                        ...prev,
                        provider: '0',
                        providerIssuedBy: prev?.providerIssuedBy || prev?.userId || currentUser.userId,
                        providerIssuedByName: prev?.providerIssuedByName || prev?.userName || currentUser.userName,
                    };
                });
                defaultProviderLoadingRef.current = false;
                return;
            }

            const providerData = await loadContactByFolderId(organizationSubjectNode);
            if (!active) return;

            defaultProviderAttemptedRef.current = true;
            setInvoice((prev: any) => {
                if (!prev) return prev;
                const currentProviderId = String(prev?.provider ?? '').trim();
                if (currentProviderId && currentProviderId !== '0') return prev;

                return {
                    ...prev,
                    provider: organizationSubjectNode,
                    providerName: providerData?.name || prev?.providerName || '',
                    providerStreet: providerData?.street || prev?.providerStreet || '',
                    providerCity: providerData?.city || prev?.providerCity || '',
                    providerZIP: providerData?.zip || prev?.providerZIP || '',
                    providerRegion: providerData?.region || prev?.providerRegion || '',
                    providerCountry: providerData?.country || prev?.providerCountry || '',
                    providerIssuedBy: prev?.providerIssuedBy || prev?.userId || currentUser.userId,
                    providerIssuedByName: prev?.providerIssuedByName || prev?.userName || currentUser.userName,
                };
            });
            defaultProviderLoadingRef.current = false;
        };

        seedDefaultProvider();
        return () => {
            active = false;
            defaultProviderLoadingRef.current = false;
        };
    }, [
        isEdit,
        invoice?.baseType,
        invoice?.isIssued,
        invoice?.provider,
        SELECTED_INVOICE?.baseType,
        SELECTED_INVOICE?.isIssued,
        firstDefinedId,
        getCurrentUserDefaults,
        getDefaultNodeByIdents,
        loadContactByFolderId,
    ]);

    useEffect(() => {
        if (isEdit) return;
        if (!invoice) return;

        const existingCurrency = String(invoice?.currency || '').trim();
        if (existingCurrency) {
            return;
        }
        if (defaultCurrencyAttemptedRef.current) {
            return;
        }
        defaultCurrencyAttemptedRef.current = true;

        let active = true;
        const applyCurrencyIfMissing = (currencyCode: string) => {
            const code = String(currencyCode || '').trim();
            if (!code || !active) return;
            setInvoice((prev: any) => {
                if (!prev) return prev;
                // Never overwrite user-selected currency.
                if (String(prev?.currency || '').trim()) return prev;
                return { ...prev, currency: code };
            });
        };

        const loadDefaultCurrency = async () => {
            const cached = readCachedDefaultCurrency();
            if (cached) {
                setDefaultOrgCurrencyCode(cached);
                applyCurrencyIfMissing(cached);
                return;
            }

            try {
                const response: any = await financeService.getFinanceSettings(
                    new GetFinanceSettingsRequest(store.Server, store.SessionId)
                );
                const settings = response?.SETTINGS;
                const defaultCurrency = String(
                    Array.isArray(settings) ? settings?.[0]?.currency : settings?.currency || ''
                ).trim();
                if (!defaultCurrency) return;
                writeCachedDefaultCurrency(defaultCurrency);
                setDefaultOrgCurrencyCode(defaultCurrency);
                applyCurrencyIfMissing(defaultCurrency);
            } catch {
                // Keep form usable even if finance settings fail.
            }
        };

        loadDefaultCurrency();
        return () => {
            active = false;
        };
    }, [
        isEdit,
        invoice?.currency,
        financeService,
        store.Server,
        store.SessionId,
        readCachedDefaultCurrency,
        writeCachedDefaultCurrency,
    ]);

    useEffect(() => {
        if (!invoice) return;
        if (manualCurrencyRateRef.current) return;

        const currencyCode = String(invoice?.currency || '').trim().toUpperCase();
        if (!currencyCode) return;

        const effectiveDate = getEffectiveCurrencyRateDate(invoice);
        const localCurrencyCode = String(
            defaultOrgCurrencyCode || readCachedDefaultCurrency() || ''
        )
            .trim()
            .toUpperCase();

        const currentKey = `${currencyCode}|${effectiveDate}|${localCurrencyCode || 'unknown'}`;

        const applyAutoCurrencyRate = (rateValue: string) => {
            const normalizedRate = String(rateValue || '').trim();
            if (!normalizedRate) return;
            autoApplyingCurrencyRateRef.current = true;
            setInvoice((prev: any) => {
                if (!prev) return prev;
                const prevCurrency = String(prev?.currency || '').trim().toUpperCase();
                const prevDate = getEffectiveCurrencyRateDate(prev);
                const prevKey = `${prevCurrency}|${prevDate}|${localCurrencyCode || 'unknown'}`;
                if (prevKey !== currentKey || manualCurrencyRateRef.current) {
                    autoApplyingCurrencyRateRef.current = false;
                    return prev;
                }
                return { ...prev, currencyRate: normalizedRate };
            });
            setTimeout(() => {
                autoApplyingCurrencyRateRef.current = false;
            }, 0);
        };

        if (localCurrencyCode && currencyCode === localCurrencyCode) {
            lastLoadedCurrencyRateKeyRef.current = currentKey;
            if (String(invoice?.currencyRate ?? '').trim() !== '1') {
                applyAutoCurrencyRate('1');
            }
            return;
        }

        const currencyRecord = getCurrencyRecordByCode(currencyCode);
        const currencyId = String(currencyRecord?.id || '').trim();
        if (!currencyId) return;

        if (
            lastLoadedCurrencyRateKeyRef.current === currentKey &&
            String(invoice?.currencyRate ?? '').trim()
        ) {
            return;
        }

        let active = true;
        const loadCurrencyRate = async () => {
            try {
                const response: any = await financeService.listCurrencyRate(
                    new ListCurrencyRateRequest(store.Server, store.SessionId, {
                        offset: '0',
                        limit: '200',
                        currency: currencyId,
                        rateDate: effectiveDate,
                    })
                );
                if (!active || response?.result !== 'OK') return;

                const bestRate = getBestCurrencyRate(response, effectiveDate);
                const resolvedRate = String(bestRate?.rate || '').trim();

                lastLoadedCurrencyRateKeyRef.current = currentKey;

                if (!resolvedRate) {
                    ToastQueue.info(
                        _t('toast_currency_rate_not_found', 'Exchange rate was not found for the selected currency/date.'),
                        { timeout: 3000 }
                    );
                    return;
                }

                applyAutoCurrencyRate(resolvedRate);
            } catch {
                if (!active) return;
                ToastQueue.info(
                    _t('toast_currency_rate_not_found', 'Exchange rate was not found for the selected currency/date.'),
                    { timeout: 3000 }
                );
            }
        };

        loadCurrencyRate();
        return () => {
            active = false;
        };
    }, [
        invoice?.currency,
        invoice?.dateTransaction,
        invoice?.taxationDate,
        invoice?.issuedDate,
        invoice?.currencyRate,
        financeService,
        store.Server,
        store.SessionId,
        getCurrencyRecordByCode,
        defaultOrgCurrencyCode,
        readCachedDefaultCurrency,
        getEffectiveCurrencyRateDate,
        getBestCurrencyRate,
        _t,
    ]);

    useEffect(() => {
        if (isEdit) return;
        if (!invoice) return;
        if (defaultCounterpartyAttemptedRef.current) return;
        if (defaultCounterpartyLoadingRef.current) return;
        const currentDocClass = getInvoiceDocumentClass(invoice || SELECTED_INVOICE || {});
        if (currentDocClass === 'unknown') return;

        const contextActivity = firstDefinedString(invoice?.activity, SELECTED_INVOICE?.activity);
        const contextProject = firstDefinedString(invoice?.project, SELECTED_INVOICE?.project);
        const contextFolder = firstDefinedString(invoice?.folder, SELECTED_INVOICE?.folder);
        if (!contextActivity && !contextProject && !contextFolder) return;

        let active = true;

        const loadCounterpartyDefaults = async () => {
            defaultCounterpartyLoadingRef.current = true;
            const activityId = toPositiveInteger(contextActivity);
            const projectId = toPositiveInteger(contextProject);
            const folderId = toPositiveInteger(contextFolder);
            const organizationSubjectNode = getDefaultNodeByIdents(['ORGANIZATIONSUBJECTNODE']);

            let activityData: any;
            if (activityId) {
                try {
                    const response: any = await projectService.getActivity(
                        new GetActivityRequest(store.Server, store.SessionId, activityId)
                    );
                    if (response?.result === 'OK') activityData = response?.ACTIVITY;
                } catch {
                    // Keep invoice form usable if context resolution fails.
                }
            }

            let projectData: any;
            if (projectId) {
                try {
                    const response: any = await projectService.getProject(
                        new GetProjectRequest(store.Server, store.SessionId, projectId)
                    );
                    if (response?.result === 'OK') projectData = response?.PROJECT;
                } catch {
                    // Keep invoice form usable if context resolution fails.
                }
            }

            let folderData: any;
            if (folderId) {
                try {
                    const response: any = await projectService.getFolder(
                        new GetFolderRequest(store.Server, store.SessionId, folderId)
                    );
                    if (response?.result === 'OK') {
                        folderData = response?.FOLDER;
                    }
                } catch {
                    // Keep invoice form usable if context resolution fails.
                }
            }

            const activitySupplier = firstDefinedId(activityData?.supplier, activityData?.provider);
            const projectSupplier = firstDefinedId(projectData?.supplier, projectData?.provider);
            const folderSupplier = firstDefinedId(folderData?.supplier, folderData?.provider);

            const activityCustomer = firstDefinedId(activityData?.customer);
            const projectCustomer = firstDefinedId(projectData?.customer);
            const folderCustomer = firstDefinedId(folderData?.customer);

            const folderContact = firstDefinedId(folderData?.id, contextFolder);
            const docClass = getInvoiceDocumentClass(invoice || SELECTED_INVOICE || {});
            const contextSupplierId = firstDefinedId(
                activitySupplier,
                projectSupplier,
                folderSupplier,
                folderContact
            );
            const contextCustomerId = firstDefinedId(
                activityCustomer,
                projectCustomer,
                folderCustomer,
                folderContact
            );

            // Our organization fallback chain from context:
            // sales docs => supplier chain, purchase docs => customer chain.
            const resolvedOurOrganizationId =
                docClass === 'sales'
                    ? firstDefinedId(activitySupplier, projectSupplier, folderSupplier, organizationSubjectNode)
                    : firstDefinedId(activityCustomer, projectCustomer, folderCustomer, organizationSubjectNode);

            // Counterparty by direction:
            // sales => customer from context
            // purchase => supplier from context
            const defaultProviderId =
                docClass === 'sales'
                    ? firstDefinedId(resolvedOurOrganizationId, organizationSubjectNode) || '0'
                    : contextSupplierId;
            const defaultCustomerId =
                docClass === 'purchase'
                    ? firstDefinedId(resolvedOurOrganizationId, organizationSubjectNode)
                    : contextCustomerId;

            const [providerData, customerData] = await Promise.all([
                loadContactByFolderId(defaultProviderId),
                loadContactByFolderId(defaultCustomerId),
            ]);

            const folderCompany = folderData?.CONTACT?.COMPANY;
            const rawFolderAddresses = folderCompany?.ADDRESSES?.ADDRESS;
            const folderAddress = Array.isArray(rawFolderAddresses)
                ? rawFolderAddresses.find((a: any) => String(a?.isPrimary ?? '') === '1') || rawFolderAddresses[0]
                : rawFolderAddresses || {};
            const folderFallbackContact = folderContact
                ? {
                    id: folderContact,
                    name: firstDefinedString(
                        folderData?.name,
                        folderData?.name1,
                        folderCompany?.name1,
                        folderCompany?.name
                    ) || '',
                    street: firstDefinedString(
                        folderAddress?.street,
                        folderCompany?.street,
                        folderData?.street
                    ) || '',
                    city: firstDefinedString(
                        folderAddress?.cityName,
                        folderAddress?.city,
                        folderCompany?.city,
                        folderData?.city
                    ) || '',
                    zip: firstDefinedString(
                        folderAddress?.zipName,
                        folderAddress?.zip,
                        folderCompany?.zip,
                        folderData?.zip
                    ) || '',
                    region: firstDefinedString(
                        folderAddress?.regionName,
                        folderAddress?.region,
                        folderCompany?.region,
                        folderData?.region
                    ) || '',
                    country: firstDefinedString(
                        folderAddress?.countryName,
                        folderAddress?.country,
                        folderCompany?.country,
                        folderData?.country
                    ) || '',
                }
                : undefined;

            const providerForDefault =
                providerData
                    ? { ...providerData, id: firstDefinedString(providerData.id, defaultProviderId) }
                    : (defaultProviderId === folderContact
                        ? folderFallbackContact
                        : (defaultProviderId
                            ? {
                                id: defaultProviderId,
                                name: '',
                                street: '',
                                city: '',
                                zip: '',
                                region: '',
                                country: '',
                            }
                            : undefined));
            const customerForDefault =
                customerData
                    ? { ...customerData, id: firstDefinedString(customerData.id, defaultCustomerId) }
                    : (defaultCustomerId === folderContact ? folderFallbackContact : undefined);

            if (!active) return;
            defaultCounterpartyAttemptedRef.current = true;
            defaultCounterpartyLoadingRef.current = false;
            setInvoice((prev: any) =>
                {
                    const withCounterparties = mergeCounterpartyDefaults(prev, {
                        provider: providerForDefault,
                        customer: customerForDefault,
                    });

                    const next = { ...withCounterparties };
                    // Preserve existing user/context values; only fill missing context labels.
                    if (!toNonEmptyString(next?.activityName) && activityData) {
                        next.activityName = firstDefinedString(
                            activityData?.name,
                            activityData?.name1,
                            activityData?.subject
                        ) || next.activityName || '';
                    }
                    if (!toNonEmptyString(next?.projectName) && projectData) {
                        next.projectName = firstDefinedString(
                            projectData?.name,
                            projectData?.name1,
                            projectData?.subject
                        ) || next.projectName || '';
                    }
                    if (!toNonEmptyString(next?.folderName) && folderData) {
                        next.folderName = firstDefinedString(
                            folderData?.name,
                            folderData?.name1,
                            folderData?.subject
                        ) || next.folderName || '';
                    }
                    return next;
                }
            );
        };

        loadCounterpartyDefaults();
        return () => {
            active = false;
            defaultCounterpartyLoadingRef.current = false;
        };
    }, [
        isEdit,
        invoice?.baseType,
        invoice?.isIssued,
        invoice?.activity,
        invoice?.project,
        invoice?.folder,
        invoice?.provider,
        invoice?.customer,
        SELECTED_INVOICE?.baseType,
        SELECTED_INVOICE?.isIssued,
        SELECTED_INVOICE?.activity,
        SELECTED_INVOICE?.project,
        SELECTED_INVOICE?.folder,
        firstDefinedString,
        firstDefinedId,
        toPositiveInteger,
        getDefaultNodeByIdents,
        projectService,
        store.Server,
        store.SessionId,
        loadContactByFolderId,
        mergeCounterpartyDefaults,
    ]);

    useEffect(() => {
        let active = true;
        const loadInvoiceFormDefaults = async () => {
            const currentInvoice = invoice || selectedInvoice || SELECTED_INVOICE;
            const baseType = String(currentInvoice?.baseType || '');
            if (baseType !== '1') return;

            const formTypeCandidates = [
                getInvoiceFormType(currentInvoice),
                'TEditInvoiceIssued',
                'TEditInvoiceReceived',
            ].filter((v, i, arr) => !!v && arr.indexOf(v) === i);

            let defaultDueDays: number | undefined;
            for (const formType of formTypeCandidates) {
                defaultDueDays = await getAddFormSettings('0', formType);
                if (defaultDueDays !== undefined) break;
            }
            if (!active || defaultDueDays === undefined) return;

            setInvoice((prev: any) => {
                if (!prev) return prev;
                const issuedDate = String(prev?.issuedDate || todayIso());
                const customerDueDaysNumeric = Math.max(0, Number(prev?.customerDueDays) || 0);
                const effectiveDays = customerDueDaysNumeric > 0 ? customerDueDaysNumeric : defaultDueDays;
                const dueDate = addDaysToIsoDate(issuedDate, effectiveDays);
                if (
                    Number(prev?.invoiceDefaultDueDays ?? -1) === Number(defaultDueDays) &&
                    String(prev?.dueDate || '') === String(dueDate || '')
                ) {
                    return prev;
                }
                return {
                    ...prev,
                    invoiceDefaultDueDays: defaultDueDays,
                    dueDate,
                };
            });
        };

        loadInvoiceFormDefaults();
        return () => {
            active = false;
        };
    }, [
        invoice?.baseType,
        invoice?.issuedDate,
        invoice?.customerDueDays,
        selectedInvoice?.id,
        SELECTED_INVOICE?.baseType,
        getAddFormSettings,
    ]);

    useEffect(() => {
        if (!invoice && !SELECTED_INVOICE) return;
        const nextOptions = getInvoiceStatusOptions(invoice || SELECTED_INVOICE);
        setInvoiceStatusOptions((prev: any[]) => (
            areStatusOptionsEqual(prev, nextOptions) ? prev : nextOptions
        ));
    }, [
        invoice?.baseType,
        invoice?.state,
        invoice?.stateName,
        invoice?.STATES,
        SELECTED_INVOICE?.baseType,
        SELECTED_INVOICE?.state,
        SELECTED_INVOICE?.stateName,
        SELECTED_INVOICE?.STATES,
        store?.session?.organization?.OBJECT_SETTINGS?.INVOICE?.INVOICE_STATES?.INVOICE_STATUS,
    ]);

    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!isDirty && !isSaving) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isDirty, isSaving]);

    const requestLeave = useCallback((leaveAction?: () => void) => {
        if (!isDirty && !isSaving) {
            leaveAction?.();
            return;
        }
        pendingLeaveActionRef.current = leaveAction || null;
        setShowLeaveDialog(true);
    }, [isDirty, isSaving]);

    const handleSave = async (options?: { onSuccessNavigate?: () => void; suppressDirtyReset?: boolean }) => {
        if (isSaving) return;
        if (!invoice) {
            ToastQueue.negative(_t('toast_invoice_data_empty', 'Invoice data is empty.'), { timeout: 2500 });
            return;
        }

        const currentInvoice = invoiceRef.current || invoice || {};
        const resolvedDefaultTreeHandle = resolveInvoiceTreeHandleFromDefaults(currentInvoice);
        const resolvedTransactionTreeHandle = resolveTransactionTreeHandleFromDefaults();
        const defaultState = resolveDefaultInvoiceState(currentInvoice, false);
        const nowIsoDate = new Date().toISOString().split('T')[0];
        const paidDefaults = isPaidState(currentInvoice)
            ? {
                toBePaidDate: currentInvoice?.toBePaidDate || nowIsoDate,
                paidManual: currentInvoice?.paidManual || currentInvoice?.toBePaid || currentInvoice?.totalWithVAT || currentInvoice?.total,
            }
            : {};

        const {
            customerDueDays: _customerDueDays,
            invoiceDefaultDueDays: _invoiceDefaultDueDays,
            ...invoiceWithoutUiFields
        } = currentInvoice;
        const resolvedJournal =
            invoiceWithoutUiFields?.journalTreeHandle
                ? undefined
                : await resolveJournalById(invoiceWithoutUiFields?.journal);

        const basePayload: Invoice = {
            ...invoiceWithoutUiFields,
            ...paidDefaults,
            baseType: invoiceWithoutUiFields?.baseType || SELECTED_INVOICE?.baseType,
            isIssued: invoiceWithoutUiFields?.isIssued || SELECTED_INVOICE?.isIssued,
            folder: invoiceWithoutUiFields?.folder || SELECTED_INVOICE?.folder,
            project: invoiceWithoutUiFields?.project || SELECTED_INVOICE?.project,
            activity: invoiceWithoutUiFields?.activity || SELECTED_INVOICE?.activity,
            draftType: isEdit ? (invoiceWithoutUiFields?.draftType || SELECTED_INVOICE?.draftType) : '1',
            isDraft: isEdit ? invoiceWithoutUiFields?.isDraft : '0',
            subject:
                invoiceWithoutUiFields?.subject ||
                (String(invoiceWithoutUiFields?.isIssued || SELECTED_INVOICE?.isIssued || '') === '1'
                    ? (invoiceWithoutUiFields?.customer || invoiceWithoutUiFields?.shipTo || invoiceWithoutUiFields?.folder || invoiceWithoutUiFields?.project || invoiceWithoutUiFields?.userId)
                    : (invoiceWithoutUiFields?.provider || invoiceWithoutUiFields?.shipTo || invoiceWithoutUiFields?.folder || invoiceWithoutUiFields?.project || invoiceWithoutUiFields?.userId)),
            journal: invoiceWithoutUiFields?.journal,
            journalTreeHandle:
                invoiceWithoutUiFields?.journalTreeHandle ||
                getJournalValue(resolvedJournal, 'treeHandle', 'treehandle') ||
                SELECTED_INVOICE?.journalTreeHandle,
            templateAcc:
                invoiceWithoutUiFields?.templateAcc ||
                SELECTED_INVOICE?.templateAcc,
            currencyRate: invoiceWithoutUiFields?.currencyRate || '1',
            taxationDate: invoiceWithoutUiFields?.taxationDate || invoiceWithoutUiFields?.issuedDate,
            dateTransaction: invoiceWithoutUiFields?.dateTransaction || invoiceWithoutUiFields?.taxationDate || invoiceWithoutUiFields?.issuedDate,
            periodFrom: invoiceWithoutUiFields?.periodFrom || invoiceWithoutUiFields?.issuedDate,
            state: invoiceWithoutUiFields?.state || defaultState.state,
            stateName: invoiceWithoutUiFields?.stateName || defaultState.stateName,
            treeHandle:
                resolvedDefaultTreeHandle ||
                invoiceWithoutUiFields?.treeHandle ||
                SELECTED_INVOICE?.treeHandle ||
                SELECTED_INVOICE?.journalTreeHandle,
            transactionTreeHandle:
                invoiceWithoutUiFields?.transactionTreeHandle ||
                SELECTED_INVOICE?.transactionTreeHandle ||
                resolvedTransactionTreeHandle ||
                getDefaultNodeByIdents(['GLITEMNODE']) ||
                getDefaultNodeByIdents(['INVOICETRANSACTIONNODE']),
            externalNumber: invoiceWithoutUiFields?.externalNumber || invoiceWithoutUiFields?.exterNalNumber || '',
        };
        if (String(basePayload?.journal || '').trim() && !String(basePayload?.journalTreeHandle || '').trim()) {
            ToastQueue.negative(
                _t('toast_missing_journal_tree_handle', 'Missing journal tree handle.'),
                { timeout: 3500 }
            );
            return;
        }
        if (!isEdit && !basePayload?.transactionTreeHandle) {
            ToastQueue.negative(_t('toast_missing_transaction_tree_handle', 'Missing transaction tree handle (GLITEMNODE).'), { timeout: 3500 });
            return;
        }
        const { payload, errors } = buildInvoicePayloadForSave(basePayload, { isCreate: !isEdit });
        if (errors.length > 0) {
            ToastQueue.negative(errors[0], { timeout: 3500 });
            return;
        }

        try {
            setIsSaving(true);
            const response = isEdit
                ? await invoiceService.updateInvoice(
                    new UpdateInvoiceRequest(store.Server, store.SessionId, payload as Invoice)
                )
                : await invoiceService.createInvoice(
                    new CreateInvoiceRequest(store.Server, store.SessionId, payload as Invoice)
                );

            if (response?.result === 'OK') {
                if (!options?.suppressDirtyReset) {
                    setIsDirty(false);
                }
                setShowLeaveDialog(false);
                ToastQueue.positive(
                    isEdit
                        ? t('invoice_messages.invoice_updated', { ns: 'invoice', defaultValue: 'Invoice updated successfully.' })
                        : t('invoice_messages.invoice_created', { ns: 'invoice', defaultValue: 'Invoice created successfully.' }),
                    { timeout: 2500 }
                );
                const responseInvoiceId =
                    (response as any)?.newId ||
                    (response as any)?.itemId ||
                    (response as any)?.id ||
                    (response as any)?.INVOICE?.id;
                const savedId = isEdit
                    ? (payload?.id || selectedInvoice?.id || responseInvoiceId)
                    : (responseInvoiceId || payload?.id || selectedInvoice?.id);
                if (options?.onSuccessNavigate) {
                    options.onSuccessNavigate();
                    return;
                }
                if (onSaveSuccess) {
                    await onSaveSuccess(savedId);
                } else {
                    handleBackClick?.();
                }
                return;
            }

            const errorMessage = response?.EXCEPTION?.reason || response?.EXCEPTION?.message || _t('toast_failed_to_save', 'Failed to save invoice.');
            ToastQueue.negative(errorMessage, { timeout: 3500 });
        } catch (error: any) {
            ToastQueue.negative(error?.message || _t('toast_failed_to_save', 'Failed to save invoice.'), { timeout: 3500 });
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <>
        <DialogContainer onDismiss={() => {
            setShowLeaveDialog(false);
            pendingLeaveActionRef.current = null;
        }}>
            {showLeaveDialog && (
                <Dialog>
                    <Heading>{_t('unsaved_changes_title', 'Unsaved changes')}</Heading>
                    <Divider />
                    <Content>
                        {_t(
                            'unsaved_changes_message',
                            'You have unsaved changes. Save before leaving?'
                        )}
                        <Flex justifyContent="end" gap="size-100" marginTop="size-200">
                            <Button
                                disabled={isSaving}
                                className={`${styles.btn} ${styles.btn_cancel}`}
                                onClick={() => {
                                    setShowLeaveDialog(false);
                                    pendingLeaveActionRef.current = null;
                                }}
                            >
                                {_t('stay', 'Stay')}
                            </Button>
                            <Button
                                disabled={isSaving}
                                className={`${styles.btn} ${styles.btn_cancel}`}
                                onClick={() => {
                                    const leaveAction = pendingLeaveActionRef.current;
                                    pendingLeaveActionRef.current = null;
                                    setShowLeaveDialog(false);
                                    setIsDirty(false);
                                    leaveAction?.();
                                }}
                            >
                                {_t('leave_without_saving', 'Leave without saving')}
                            </Button>
                            <Button
                                disabled={isSaving}
                                className={`${styles.btn} ${styles.btn_add}`}
                                onClick={() => {
                                    const leaveAction = pendingLeaveActionRef.current;
                                    handleSave({
                                        onSuccessNavigate: () => {
                                            pendingLeaveActionRef.current = null;
                                            leaveAction?.();
                                        },
                                    });
                                }}
                            >
                                {_tt('button_save')}
                            </Button>
                        </Flex>
                    </Content>
                </Dialog>
            )}
        </DialogContainer>
        <Flex width="100%" direction={'column'}>
            <Flex width="100%" direction={'column'} UNSAFE_style={{ padding: '10px' }}>
                <View UNSAFE_className={styles.header_main}>
                    <View UNSAFE_className={styles.header_child}>
                        <Flex direction="row" alignItems="center" justifyContent="space-between">
                            <View UNSAFE_className={styles.frame_parent}>
                                <View UNSAFE_className={styles.icon_activity_parent}>
                                    {handleBackClick &&
                                        <ActionButton isQuiet onPress={() => requestLeave(handleBackClick)} UNSAFE_className={styles.back_button}>
                                            <ChevronLeft size={20} />
                                        </ActionButton>
                                    }
                                    <b className={styles.activity_name}>
                                        {getHeaderTitle()}
                                    </b>
                                </View>
                            </View>
                            {!isEdit && (
                                <Flex direction="row" alignItems="center" gap="size-200" UNSAFE_className={styles.header_right_controls}>
                                    <View width="size-5000">
                                        <ContextSelection
                                            key={`invoice-context-${String(invoice?.id || selectedInvoice?.id || SELECTED_INVOICE?.id || '')}`}
                                            value={invoiceContextSelection || contextSelectionFallbackValue}
                                            onSelectionChange={handleContextSelectionChange}
                                            folderStatusMap={folderStatusMapping}
                                            projectStatusMap={projectStatusMapping}
                                            activityStatusMap={activityStatusMapping}
                                            defaultValue={contextSelectionDefaultValue}
                                            explicitContextId={invoiceContextSelection ? null : contextSelectionDefaultValue}
                                            onExplicitContextResolveError={onContextPathResolveError}
                                            allowClearSelection
                                            allowTransparentBG
                                            filledBackground={false}
                                            showBorders={false}
                                            aria-label={_t('context', 'Context')}
                                        />
                                    </View>
                                    <Flex direction="row" alignItems="center" gap="size-100" UNSAFE_className={styles.add_status_holder}>
                                        <span className={styles.status}>{_t('status', 'Status')}</span>
                                        <span className={styles.statusIndicator} style={{ backgroundColor: '#1976D2' }} />
                                        <span className={styles.status}>{_t('issued', 'Issued')}</span>
                                    </Flex>
                                </Flex>
                            )}
                            {isEdit && (
                                <Flex direction="row" alignItems="center" gap="size-200" UNSAFE_className={styles.header_right_controls}>
                                    <View width="size-5000">
                                        <ContextSelection
                                            key={`invoice-context-${String(invoice?.id || selectedInvoice?.id || SELECTED_INVOICE?.id || '')}`}
                                            value={invoiceContextSelection || contextSelectionFallbackValue}
                                            onSelectionChange={handleContextSelectionChange}
                                            folderStatusMap={folderStatusMapping}
                                            projectStatusMap={projectStatusMapping}
                                            activityStatusMap={activityStatusMapping}
                                            defaultValue={contextSelectionDefaultValue}
                                            explicitContextId={invoiceContextSelection ? null : contextSelectionDefaultValue}
                                            onExplicitContextResolveError={onContextPathResolveError}
                                            allowClearSelection
                                            allowTransparentBG
                                            filledBackground={false}
                                            showBorders={false}
                                            aria-label={_t('context', 'Context')}
                                        />
                                    </View>
                                    <Picker
                                        aria-label={_t('status', 'Status')}
                                        placeholder={_t('status', 'Status')}
                                        selectedKey={invoice?.state ? String(invoice.state) : null}
                                        onSelectionChange={(key) => {
                                            const statusId = key != null ? String(key) : '';
                                            const selectedStatusObj = (invoiceStatusOptions || []).find(
                                                (s: any) => String(s?.id) === statusId
                                            );
                                            setInvoiceWithRef((prev: any) => ({
                                                ...(prev || {}),
                                                state: statusId,
                                                stateName: selectedStatusObj?.name || prev?.stateName || '',
                                            }));
                                        }}
                                        width="size-2400"
                                    >
                                        {(invoiceStatusOptions || []).map((status: any) => (
                                            <Item key={String(status.id)}>{status.name}</Item>
                                        ))}
                                    </Picker>
                                </Flex>
                            )}
                        </Flex>
                    </View>
                </View>
            </Flex>
            <View width={'100%'} UNSAFE_style={{ paddingLeft: '15px', paddingRight: '15px' }}>
                <Flex direction={'row'} gap={'size-100'}>
                    <AddInvoiceDetail SELECTED_INVOICE={SELECTED_INVOICE} isEdit={!!isEdit} isLocked={false} invoice={invoice} onChange={handleInvoiceDetailChange} />
                </Flex>
                <Flex direction={'row'} gap={'size-100'}>
                    <AddSupplierDetail invoice={invoice} isEdit={!!isEdit} onChange={setInvoiceWithRef} />
                    <AddBankInfo invoice={invoice} isEdit={!!isEdit} onChange={setInvoiceWithRef} />
                </Flex>
                <Flex direction={'row'} gap={'size-100'}>
                    <InvoiceCustomerSection invoice={invoice} isEdit={!!isEdit} onChange={setInvoiceWithRef} SELECTED_INVOICE={SELECTED_INVOICE} />
                    <InvoiceShippingSection invoice={invoice} isEdit={!!isEdit} onChange={setInvoiceWithRef} />
                </Flex>
                <Flex direction={'row'} gap={'size-100'}>
                    <InvoiceFinanceSection invoice={invoice} isEdit={!!isEdit} onChange={setInvoiceWithRef} />
                    <InvoiceCustomAttributesSection invoice={invoice} isEdit={!!isEdit} onChange={setInvoiceWithRef} />
                </Flex>
                <Flex>
                    <InvoiceCommentSection invoice={invoice} isEdit={!!isEdit} onChange={setInvoiceWithRef} />
                </Flex>
                <Flex gap={'size-100'} marginTop={'size-100'} width={'100%'}>
                    <InvoiceTransactionComponent invoice={invoice} isEdit={!!isEdit} isLocked={false} onInvoiceChange={setInvoiceWithRef} />
                </Flex>
            </View>
            <Flex
                UNSAFE_style={{
                    backgroundColor: "#FFF",
                    justifyContent: "end",
                    width: "100%",
                    padding: "0.6rem",
                    paddingRight: 10,
                    borderTop: "2px solid var(--neutral-10, #ECEFF1)",
                    position: "sticky",
                    bottom: 0,
                    marginTop: "70px",
                    zIndex: 1,
                }}
            >
                <Flex gap={"size-200"} UNSAFE_className={styles.button_container}>
                    {isSaving && (
                        <Spinner
                            animation="border"
                            role="status"
                            size="sm"
                            style={{ alignSelf: 'center' }}
                        />
                    )}
                    <Button
                        variant="danger"
                        disabled={isSaving}
                        onClick={() => requestLeave(handleBackClick)}
                        className={`${styles.btn} ${styles.btn_cancel}`}
                    >
                        {_tt("button_cancel")}
                    </Button>
                    <Button
                        type="submit"
                        disabled={isSaving}
                        onClick={() => { handleSave() }}
                        className={`${styles.btn} ${styles.btn_add}`}
                    >
                        {_tt("button_save")}
                    </Button>
                </Flex>
            </Flex>
        </Flex>
        </>
    );
};

export default AddUpdateInvoice;
